import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Truck, Gift, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function StoreSettings() {
  const settings = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      free_shipping_threshold: settings.free_shipping_threshold ?? 2000,
      shipping_fee: settings.shipping_fee ?? 49,
      outside_zone_fee: settings.outside_zone_fee ?? 98,
      gift_box_price: settings.gift_box_price ?? 549,
      bulk_box_price: settings.bulk_box_price ?? 999,
      allowed_cities: [...(settings.allowed_cities || ["Delhi NCR"])],
      announcement_lines: [...(settings.announcement_lines || [])],
      delivery_note: settings.delivery_note || "",
      brand_email: settings.brand_email || "",
      brand_phone: settings.brand_phone || "",
      hero_image_url: settings.hero_image_url || "",
      instagram_url: settings.instagram_url || "",
      facebook_url: settings.facebook_url || "",
    });
  }, [settings.free_shipping_threshold, settings.shipping_fee, settings.outside_zone_fee, settings.gift_box_price, settings.bulk_box_price, settings.delivery_note, settings.allowed_cities, settings.announcement_lines, settings.brand_email, settings.brand_phone, settings.hero_image_url, settings.instagram_url, settings.facebook_url]);

  if (!form) return <div>Loading...</div>;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? "" : parseFloat(e.target.value) }));

  const setCityAt = (i, v) => setForm((f) => ({ ...f, allowed_cities: f.allowed_cities.map((c, idx) => idx === i ? v : c) }));
  const removeCity = (i) => setForm((f) => ({ ...f, allowed_cities: f.allowed_cities.filter((_, idx) => idx !== i) }));
  const addCity = () => setForm((f) => ({ ...f, allowed_cities: [...f.allowed_cities, ""] }));

  const setAnnouncementAt = (i, v) => setForm((f) => ({ ...f, announcement_lines: f.announcement_lines.map((c, idx) => idx === i ? v : c) }));
  const removeAnnouncement = (i) => setForm((f) => ({ ...f, announcement_lines: f.announcement_lines.filter((_, idx) => idx !== i) }));
  const addAnnouncement = () => setForm((f) => ({ ...f, announcement_lines: [...f.announcement_lines, ""] }));

  const save = async () => {
    const payload = {
      ...form,
      allowed_cities: form.allowed_cities.map((c) => c.trim()).filter(Boolean),
      announcement_lines: form.announcement_lines.map((c) => c.trim()).filter(Boolean),
    };
    if (!payload.allowed_cities.length) { toast.error("Add at least one delivery city/zone"); return; }
    if (!payload.announcement_lines.length) { toast.error("Add at least one banner line"); return; }
    setSaving(true);
    try {
      await api.patch("/admin/settings", payload);
      await settings.refresh();
      toast.success("Settings saved. Site updates immediately.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="store-settings">
      {/* Pack prices */}
      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-[#9E3B21]" />
          <h3 className="font-serif text-xl font-semibold">Pack Prices</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Gift Box (3 pickles) price ₹</Label>
            <Input data-testid="settings-gift-price" type="number" value={form.gift_box_price} onChange={num("gift_box_price")} className="mt-1.5" />
          </div>
          <div>
            <Label>Family Pack (6 pickles) price ₹</Label>
            <Input data-testid="settings-bulk-price" type="number" value={form.bulk_box_price} onChange={num("bulk_box_price")} className="mt-1.5" />
          </div>
        </div>
      </div>

      {/* Brand contact + hero */}
      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-[#9E3B21]" />
          <h3 className="font-serif text-xl font-semibold">Brand Contact & Homepage Hero</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Brand email</Label>
            <Input data-testid="settings-brand-email" type="email" value={form.brand_email} onChange={upd("brand_email")} placeholder="hello@dadusachaar.com" className="mt-1.5" />
          </div>
          <div>
            <Label>Brand phone (10-digit)</Label>
            <Input data-testid="settings-brand-phone" value={form.brand_phone} onChange={upd("brand_phone")} placeholder="9876543210" className="mt-1.5" />
          </div>
          <div>
            <Label>Instagram URL</Label>
            <Input data-testid="settings-instagram-url" value={form.instagram_url} onChange={upd("instagram_url")} placeholder="https://instagram.com/dadusachaar" className="mt-1.5" />
          </div>
          <div>
            <Label>Facebook URL</Label>
            <Input data-testid="settings-facebook-url" value={form.facebook_url} onChange={upd("facebook_url")} placeholder="https://facebook.com/dadusachaar" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Homepage hero image URL (paste an image URL — upload via the Products tab to get a URL)</Label>
            <Input data-testid="settings-hero-url" value={form.hero_image_url} onChange={upd("hero_image_url")} placeholder="/api/files/... or https://..." className="mt-1.5" />
            {form.hero_image_url && (
              <img src={form.hero_image_url.startsWith("http") ? form.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${form.hero_image_url}`} alt="Hero preview" className="mt-2 w-32 h-40 rounded-lg object-cover" />
            )}
          </div>
        </div>
      </div>

      {/* Delivery */}
      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Truck className="w-5 h-5 text-[#9E3B21]" />
          <h3 className="font-serif text-xl font-semibold">Delivery & Shipping</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Free shipping threshold ₹</Label>
            <Input data-testid="settings-threshold" type="number" value={form.free_shipping_threshold} onChange={num("free_shipping_threshold")} className="mt-1.5" />
            <p className="text-xs text-[#6E675F] mt-1">Orders above this get free delivery inside zone</p>
          </div>
          <div>
            <Label>In-zone shipping fee ₹</Label>
            <Input data-testid="settings-fee" type="number" value={form.shipping_fee} onChange={num("shipping_fee")} className="mt-1.5" />
          </div>
          <div>
            <Label>Outside-zone fee ₹</Label>
            <Input data-testid="settings-outside" type="number" value={form.outside_zone_fee} onChange={num("outside_zone_fee")} className="mt-1.5" />
          </div>
        </div>
        <div className="mt-5">
          <Label>Delivery cities / zones</Label>
          <p className="text-xs text-[#6E675F] mt-0.5 mb-2">Customers can select from these on checkout.</p>
          <div className="space-y-2" data-testid="settings-cities">
            {form.allowed_cities.map((c, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={c}
                  onChange={(e) => setCityAt(i, e.target.value)}
                  placeholder="e.g. Delhi NCR"
                  data-testid={`settings-city-${i}`}
                />
                <button type="button" onClick={() => removeCity(i)} className="p-2 text-[#9E3B21] hover:bg-[#9E3B21]/10 rounded-lg" data-testid={`settings-city-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addCity} className="mt-2 rounded-full" data-testid="settings-city-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add city/zone
          </Button>
        </div>
        <div className="mt-5">
          <Label>Delivery note (shown on Contact page)</Label>
          <Textarea data-testid="settings-delivery-note" value={form.delivery_note} onChange={upd("delivery_note")} rows={2} className="mt-1.5" />
        </div>
      </div>

      {/* Banner */}
      <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-5 h-5 text-[#9E3B21]" />
          <h3 className="font-serif text-xl font-semibold">Announcement Ticker</h3>
        </div>
        <p className="text-xs text-[#6E675F] mb-3">These lines scroll in the top banner across every page.</p>
        <div className="space-y-2" data-testid="settings-announcements">
          {form.announcement_lines.map((a, i) => (
            <div key={i} className="flex gap-2">
              <Input value={a} onChange={(e) => setAnnouncementAt(i, e.target.value)} data-testid={`settings-announcement-${i}`} />
              <button type="button" onClick={() => removeAnnouncement(i)} className="p-2 text-[#9E3B21] hover:bg-[#9E3B21]/10 rounded-lg" data-testid={`settings-announcement-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addAnnouncement} className="mt-2 rounded-full" data-testid="settings-announcement-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add banner line
        </Button>
      </div>

      <div className="flex justify-end">
        <Button data-testid="settings-save-all" onClick={save} disabled={saving} className="btn-primary rounded-full px-8 py-6">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save all settings"}
        </Button>
      </div>
    </div>
  );
}
